import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer, CommonCss, Card } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import {
  ORG_LEVEL,
  BUDGET_ROLE_OPTIONS,
  YN_OPTIONS,
  loadDepts,
  loadOrgLevelOptions,
  getOrgLevelOptions,
  filterDepts,
  enrichDeptRows,
  persistDeptsWithHistory,
  loadDeptHistory,
  validateDepts,
  nextSortOrderBy10,
  createDeptUid,
} from 'master/deptStore';
import {
  ACCOUNTING_MASTER_CHANGED_EVENT,
  acctUnitOptions,
} from 'budget/budgetAcctStore';
import AiDeptChatPanel from 'components/ai/AiDeptChatPanel';

const DEPT_GRID_ID = 'master-dept';
const HIST_GRID_ID = 'master-dept-history';

const WorkRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 8px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  flex: ${(p) => p.$flex || 1};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Meta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 4px;
  flex-shrink: 0;
`;

/**
 * 기준정보 — 부서관리
 * 단일 부서 마스터(상위부서·예산역할) + 조직변경 이력
 */
const DeptManageInner = () => {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) =>
      id === HIST_GRID_ID ? '이력은 조회 전용입니다. 부서 그리드에서 추가하세요.' : null,
    guardDelete: (id) => (id === HIST_GRID_ID ? '이력은 삭제할 수 없습니다.' : null),
  });
  const deptGridRef = useRef(null);
  const histGridRef = useRef(null);
  const [selectedDeptUid, setSelectedDeptUid] = useState('');
  const [selectedHint, setSelectedHint] = useState('부서를 선택하면 조직변경 이력이 표시됩니다.');
  const [acctMasterTick, setAcctMasterTick] = useState(0);
  const [orgLevelOptions, setOrgLevelOptions] = useState(() => getOrgLevelOptions());
  const defaultOrgLevel = orgLevelOptions[orgLevelOptions.length - 1]?.value || ORG_LEVEL.TEAM;

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      deptCode: '',
      deptName: '',
      orgLevel: '',
      budgetRole: '',
      useYn: 'Y',
    }),
    [tenantCode]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const opts = await loadOrgLevelOptions();
      if (!cancelled) setOrgLevelOptions(opts);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const refresh = (e) => {
      if (e.detail?.tenantCode === tenantCode && e.detail?.axis === 'unit') {
        setAcctMasterTick((n) => n + 1);
      }
    };
    window.addEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
  }, [tenantCode]);

  const unitOptions = useMemo(
    () => acctUnitOptions(tenantCode),
    [tenantCode, acctMasterTick]
  );

  const deptColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '부서코드', field: 'deptCode', size: 100, required: true }),
      hiCol.edTextColumn({ header: '부서명', field: 'deptName', size: 140, required: true }),
      hiCol.edTextColumn({
        header: '상위부서',
        field: 'parentDeptCode',
        size: 100,
        title: '상위 부서코드 (없으면 최상위)',
      }),
      hiCol.edComboColumn({
        header: '조직레벨',
        field: 'orgLevel',
        size: 100,
        options: orgLevelOptions,
        required: true,
        title: '공통코드 orglevel',
      }),
      hiCol.edComboColumn({
        header: '예산역할',
        field: 'budgetRole',
        size: 120,
        options: BUDGET_ROLE_OPTIONS,
        required: true,
        title: '편성 / 집행 / 편성+집행',
      }),
      // 회계단위(사업장) — 기관별 회계단위등록 마스터 사용
      hiCol.edComboColumn({
        header: '회계단위',
        field: 'acctUnit',
        size: 120,
        options: unitOptions,
        title: '회계단위등록에서 관리하는 본점·지점·센터 등 사업장 단위',
      }),
      hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 180 }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 80, options: YN_OPTIONS }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 80, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    [unitOptions, orgLevelOptions]
  );

  const histColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '변경일시', field: 'changeAtDisp', size: 150 }),
      hiCol.roTextColumn({ header: '유형', field: 'changeTypeNm', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 90 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 120 }),
      hiCol.roTextColumn({ header: '상위', field: 'parentDeptCode', size: 90 }),
      hiCol.roTextColumn({ header: '레벨', field: 'orgLevelNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '예산역할', field: 'budgetRoleNm', size: 100 }),
      hiCol.roTextColumn({ header: '회계단위', field: 'acctUnitNm', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '사용', field: 'useYn', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 160 }),
      hiCol.roTextColumn({ header: '처리자', field: 'changeBy', size: 90 }),
      hiCol.roTextColumn({ header: '메모', field: 'memo', size: 200 }),
    ],
    []
  );

  const deptGrid = useHiGridHook({
    gridRef: deptGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      deptUid: '',
      deptCode: '',
      deptName: '',
      parentDeptCode: '',
      orgLevel: ORG_LEVEL.TEAM,
      budgetRole: 'EXEC',
      acctUnit: 'HQ',
      useYn: 'Y',
      sortOrder: 10,
      pathCode: '',
      remark: '',
    },
    getDefaultRow: () => ({
      orgLevel: defaultOrgLevel,
      acctUnit: unitOptions[0]?.value || '',
    }),
  });

  const histGrid = useHiGridHook({
    gridRef: histGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const pushHistory = useCallback(
    (deptUid, rowHint) => {
      if (!deptUid) {
        histGridRef.current?.setData?.([]);
        setSelectedHint('부서를 선택하면 조직변경 이력이 표시됩니다.');
        return;
      }
      const tenant = getValues('tenantCode') || tenantCode;
      const list = loadDeptHistory(tenant, deptUid).map((h) => ({
        ...h,
        deptCode: h.snapshot?.deptCode || '',
        deptName: h.snapshot?.deptName || '',
        parentDeptCode: h.snapshot?.parentDeptCode || '',
        useYn: h.snapshot?.useYn || '',
        pathCode: h.snapshot?.pathCode || '',
      }));
      histGridRef.current?.setData?.(list);
      if (rowHint) {
        setSelectedHint(
          `${rowHint.deptCode || ''} ${rowHint.deptName || ''} · 경로 ${rowHint.pathCode || '—'}`
        );
      }
    },
    [getValues, tenantCode]
  );

  const selectDeptRow = useCallback(
    (row) => {
      if (!row?.deptUid) return;
      setSelectedDeptUid(row.deptUid);
      pushHistory(row.deptUid, row);
    },
    [pushHistory]
  );

  const handleSearch = useCallback(async () => {
    try {
      const tenant = getValues('tenantCode') || tenantCode;
      const all = await loadDepts(tenant);
      const filtered = enrichDeptRows(filterDepts(all, getValues()), tenant);
      deptGridRef.current?.setData?.(filtered);
      const still = filtered.find((r) => r.deptUid === selectedDeptUid);
      if (still) {
        pushHistory(still.deptUid, still);
      } else {
        setSelectedDeptUid('');
        pushHistory('');
      }
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [getValues, tenantCode, selectedDeptUid, pushHistory]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  useEffect(() => {
    deptGrid.onSelectRow = (row) => selectDeptRow(row);
    deptGrid.onRowClicked = ({ data }) => selectDeptRow(data);
  }, [deptGrid, selectDeptRow]);

  const handleAdd = useCallback(async () => {
    const data = deptGridRef.current?.getData?.() || [];
    const sortOrder = nextSortOrderBy10(data);
    const parent =
      data.find((r) => r.deptUid === selectedDeptUid)?.deptCode ||
      data.find((r) => r.budgetRole === 'BUDGET' || r.budgetRole === 'BOTH')?.deptCode ||
      '';
    await deptGrid.handleAdd({
      deptUid: createDeptUid(),
      deptCode: '',
      deptName: '',
      parentDeptCode: parent,
      orgLevel: defaultOrgLevel,
      budgetRole: 'EXEC',
      acctUnit: unitOptions[0]?.value || '',
      useYn: 'Y',
      sortOrder,
      pathCode: '',
      remark: '',
    });
  }, [deptGrid, selectedDeptUid, unitOptions, defaultOrgLevel]);

  const handleDelete = useCallback(async () => {
    await deptGrid.handleDelete();
  }, [deptGrid]);

  const handleSave = useCallback(async () => {
    try {
      const tenant = getValues('tenantCode') || tenantCode;
      const gridData = deptGridRef.current?.getData?.() || [];
      const active = gridData.filter((r) => r.mod !== 'D');
      const v = validateDepts(active);
      if (!v.ok) {
        showAlert(v.message, 'warning');
        return;
      }

      const all = await loadDepts(tenant);
      const filterVals = getValues();
      const inFilterUids = new Set(filterDepts(all, filterVals).map((r) => r.deptUid));
      const gridUidSet = new Set(gridData.map((r) => r.deptUid).filter(Boolean));
      // 필터 밖 행은 유지, 필터 안·그리드 활성 행만 반영 (mod D·제거 행은 제외 → 이력 CLOSE)
      const outside = all.filter((r) => !inFilterUids.has(r.deptUid));
      const afterDelete = enrichDeptRows([...outside, ...active], tenant);

      const { saved, historyTouched } = await persistDeptsWithHistory(tenant, afterDelete, {
        userId: user?.id || user?.username || '',
      });
      // 그리드에 없던(필터 내 삭제) uid도 afterDelete에 없으면 CLOSE 처리됨
      void gridUidSet;

      deptGridRef.current?.setData?.(
        enrichDeptRows(filterDepts(saved, getValues()), tenant)
      );
      if (selectedDeptUid) {
        const row = saved.find((r) => r.deptUid === selectedDeptUid);
        pushHistory(selectedDeptUid, row);
      }
      showAlert(
        historyTouched > 0
          ? `저장했습니다. 이력 ${historyTouched}건이 기록되었습니다.`
          : '저장했습니다.',
        'success'
      );
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [getValues, tenantCode, user, selectedDeptUid, pushHistory]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'deptCode',
          label: '부서코드',
          type: 'text',
          width: CommonCss.w80,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'deptName',
          label: '부서명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'orgLevel',
          label: '레벨',
          type: 'select',
          width: CommonCss.w90,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...orgLevelOptions],
        },
        {
          name: 'budgetRole',
          label: '예산역할',
          type: 'select',
          width: CommonCss.w110,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...BUDGET_ROLE_OPTIONS],
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          width: CommonCss.w70,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
        },
      ],
    ],
    [orgLevelOptions]
  );

  const getDeptExportData = useCallback(
    () => deptGridRef.current?.getFilteredData?.() ?? deptGridRef.current?.getData?.() ?? [],
    []
  );
  const getDeptExportCols = useCallback(() => deptGridRef.current?.getDataColumns?.() ?? [], []);
  const getHistExportData = useCallback(
    () => histGridRef.current?.getFilteredData?.() ?? histGridRef.current?.getData?.() ?? [],
    []
  );
  const getHistExportCols = useCallback(() => histGridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: DEPT_GRID_ID,
    label: '부서',
    getData: getDeptExportData,
    getDataColumns: getDeptExportCols,
    joinKeys: ['deptUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: HIST_GRID_ID,
    label: '부서이력',
    getData: getHistExportData,
    getDataColumns: getHistExportCols,
    joinKeys: ['histId'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '부서관리',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, openExcelDialog, activeLabel]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="master-dept-manage"
          prefsUserId={prefsUserId}
          summary="부서·예산역할 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성 그리드: <strong style={{ color: 'var(--erp-color-primary)' }}>{activeLabel || '—'}</strong>
        {' · '}
        Alt+F2 순환
        {activeGridId ? ` · F8/F9 → ${activeLabel}` : ''}
        {' · '}
        기관 {getValues('tenantCode') || tenantCode}
      </Meta>

      <WorkRow>
      <Stack>
        <Panel $flex={1.4}>
          <ActiveGridHeader gridId={DEPT_GRID_ID} extra="상위부서·예산역할(편성/집행/편성+집행)">
            부서
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={DEPT_GRID_ID}
              label="부서"
              order={0}
              onAdd={handleAdd}
              onDelete={handleDelete}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={DEPT_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={deptGridRef}
                  columns={deptColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === DEPT_GRID_ID}
                  {...deptGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="부서"
                  gridId={DEPT_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={deptGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={0.9}>
          <ActiveGridHeader gridId={HIST_GRID_ID} extra="신규·수정·상위변경·역할변경·폐지">
            부서 이력
          </ActiveGridHeader>
          <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>
          <GridBody>
            <ActiveGridScope
              gridId={HIST_GRID_ID}
              label="부서이력"
              order={1}
              canAdd={false}
              canDelete={false}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={HIST_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={histGridRef}
                  columns={histColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={48}
                  headerActive={activeGridId === HIST_GRID_ID}
                  {...histGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="부서이력"
                  gridId={HIST_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>
      <AiDeptChatPanel prefsUserId={prefsUserId} onMutated={handleSearch} />
      </WorkRow>

      {excelDialog}
    </PageContainer>
  );
};

const DeptManage = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <DeptManageInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default DeptManage;
